import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { useNavigate } from 'react-router-dom';

interface NewCamperRecord {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  placeName: string;
  arrivalDate: string;
  departureDate: string;
}

const formatReportDate = (value: any): string => {
  if (!value) return '';
  const date = new Date(value);
  return isNaN(date.getTime()) ? '' : date.toLocaleDateString();
};

const NewCamperReport: React.FC = () => {
  const [newCampers, setNewCampers] = useState<NewCamperRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchNewCampers = async () => {
      setLoading(true);
      setError('');
      try {
        const registrationsRef = collection(db, 'Registrations');
        const q = query(registrationsRef, where('NewCamper', '==', true));
        const snapshot = await getDocs(q);
        const results: NewCamperRecord[] = [];
        
        snapshot.forEach(docSnap => {
          const data = docSnap.data();

          // Only include new campers who have been assigned a place to stay.
          if (data.status !== 'Assigned') {
            return;
          }
          
          // Extract name from different possible structures
          const primaryCamperName = data.registrationInfo?.primaryCamperName || '';
          const nameParts = primaryCamperName.split(' ');
          const firstName = nameParts[0] || data.firstName || '';
          const lastName = nameParts.slice(1).join(' ') || data.lastName || '';
          const email = data.registrationInfo?.email || data.email || '';
          
          results.push({
            id: docSnap.id,
            firstName,
            lastName,
            email,
            placeName: data.assignedAccommodation?.placeName || data.accommodationFees?.placeName || data.financial?.accommodationFees?.placeName || '',
            arrivalDate: data.campDates?.arrivalDate || '',
            departureDate: data.campDates?.departureDate || '',
          });
        });
        
        setNewCampers(results);
      } catch (err) {
        setError('Failed to load new campers report');
        console.error('Error fetching new campers:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchNewCampers();
  }, []);

  const handleRowClick = (registrationId: string) => {
    if (registrationId) {
      navigate(`/admin/assignments?registrationId=${registrationId}`);
    }
  };

  const handleExportCSV = () => {
    const headers = ['First Name', 'Last Name', 'Email', 'Place Name', 'Arrival Date', 'Departure Date'];
    const escape = (value: string) => `"${String(value).replace(/"/g, '""')}"`;
    const csvRows = [
      headers.join(','),
      ...newCampers.map(camper =>
        [
          escape(camper.firstName),
          escape(camper.lastName),
          escape(camper.email),
          escape(camper.placeName),
          escape(formatReportDate(camper.arrivalDate)),
          escape(formatReportDate(camper.departureDate)),
        ].join(',')
      ),
    ];
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `new-campers-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="new-camper-report">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>New Camper Report</h2>
        {newCampers.length > 0 && (
          <button
            style={{
              padding: '8px 16px',
              background: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 500,
              fontSize: '0.9rem',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#218838')}
            onMouseLeave={(e) => (e.currentTarget.style.background = '#28a745')}
            onClick={handleExportCSV}
          >
            Export to CSV
          </button>
        )}
      </div>
      <p style={{ color: '#666', marginBottom: '16px' }}>
        Showing all registrations where the NewCamper field is set to True
      </p>
      {loading ? (
        <div>Loading...</div>
      ) : error ? (
        <div style={{ color: 'red' }}>{error}</div>
      ) : newCampers.length === 0 ? (
        <div>No new campers found.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 16 }}>
          <thead>
            <tr>
              <th>First Name</th>
              <th>Last Name</th>
              <th>Email</th>
              <th>Place Name</th>
              <th>Arrival Date</th>
              <th>Departure Date</th>
            </tr>
          </thead>
          <tbody>
            {newCampers.map(camper => (
              <tr 
                key={camper.id} 
                style={{ 
                  cursor: 'pointer', 
                  borderBottom: '1px solid #ddd',
                  padding: '8px'
                }} 
                onClick={() => handleRowClick(camper.id)}
              >
                <td style={{ padding: '8px' }}>{camper.firstName}</td>
                <td style={{ padding: '8px' }}>{camper.lastName}</td>
                <td style={{ padding: '8px' }}>{camper.email}</td>
                <td style={{ padding: '8px' }}>{camper.placeName}</td>
                <td style={{ padding: '8px' }}>{formatReportDate(camper.arrivalDate)}</td>
                <td style={{ padding: '8px' }}>{formatReportDate(camper.departureDate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default NewCamperReport; 